import React, { useCallback, useMemo } from "react";

import { BaseLinkContextProvider } from "shared/components/common/base-link/context";

import { partitionSitemapSections } from "scmp-app/components/footer/sitemap/helpers";
import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import { pathname as newsletterPathname } from "scmp-app/pages/newsletters";
import { useNewsletterUrl } from "scmp-app/pages/newsletters/hooks";

import type { SitemapItemLink } from "./hooks";
import { useSitemap } from "./hooks";
import {
  ColumnContainer,
  Container,
  Item,
  List,
  SectionContainer,
  SectionLabel,
  StyledBaseLink,
} from "./styles";

export type Props = {
  className?: string;
};

type ConditionalProperty = {
  target: string;
  url: string;
};

export const FooterSitemap: React.FunctionComponent<Props> = ({ className }) => {
  const { sitemap } = useSitemap();
  const { newsletterUrl } = useNewsletterUrl();

  const renderAnchor = useCallback(
    (link: SitemapItemLink) => {
      const linkUrl = link.url.alias === newsletterPathname ? newsletterUrl : link.url.alias;
      const conditionalProperty: ConditionalProperty = {
        target: link.isExternal ? "_blank" : "",
        url: link.isExternal
          ? `${link.url.alias}${link.url.alias.includes("?") ? "&" : "?"}utm_source=scmp_footer`
          : linkUrl,
      };

      const baseLink = (
        <StyledBaseLink
          onClick={() => {
            sendGA4Tracking({
              action: "click",
              category: "footer",
              customized_parameters: {
                link_name: link.label,
              },
              subcategory: "",
            });
          }}
          pathname={conditionalProperty.url}
          target={conditionalProperty.target}
        >
          {link.label}
        </StyledBaseLink>
      );

      if (link.isExternal) return baseLink;

      const footerCustomQueryParameters = {
        module: "footer",
      };

      return (
        <BaseLinkContextProvider
          customQueryParameters={link.customQueryParameters ?? footerCustomQueryParameters}
        >
          {baseLink}
        </BaseLinkContextProvider>
      );
    },
    [newsletterUrl],
  );

  // 4 columns, configured as [[1, 2], [3], [4, 5], [6, 7]]
  const partitionedSections = partitionSitemapSections(sitemap.sections, [2, 1, 2, 2]);

  const sitemapElement = useMemo(
    () =>
      partitionedSections.map((column, columnIndex) => (
        <ColumnContainer key={columnIndex}>
          {column.map(section => {
            if ("label" in section) {
              return (
                <SectionContainer key={section.label}>
                  <SectionLabel>
                    {section.labelLink
                      ? renderAnchor({
                          isExternal: true,
                          label: section.label,
                          url: {
                            alias: section.labelLink,
                          },
                        })
                      : section.label}
                  </SectionLabel>
                  <List>
                    {section.links.map((link, index) => (
                      <Item key={index}>{renderAnchor(link)}</Item>
                    ))}
                  </List>
                </SectionContainer>
              );
            }
          })}
        </ColumnContainer>
      )),
    [renderAnchor, partitionedSections],
  );

  return <Container className={className}>{sitemapElement}</Container>;
};

FooterSitemap.displayName = "FooterSitemap";
